import type { KpiSignal } from './types';
import { latestAsOf, previousBeforeAsOf } from './timeSeries';
import { KPI_DEFAULTS } from './defaults';

export type ProgressPoint = {
  project_GUID: string;
  reporting_date: string;
  baseline_complete: number;
  actual_complete: number;
  forecast_complete?: number;
};

export type ScheduleToleranceConfig = {
  yellow_threshold_pp: number; // behind >= yellow
  red_threshold_pp: number; // behind >= red
  // Score mapping: at or beyond this many pp behind -> score 0.
  score_zero_at_behind_pp?: number;
  // Small epsilon for trend flatness.
  trend_epsilon_pp?: number;
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function indicatorColorValueFromBehind(behindPP: number, cfg: ScheduleToleranceConfig): number {
  if (!Number.isFinite(behindPP)) return 0;
  // Discrete UI codes: 0=no-data, 1=red, 2=yellow, 3=green.
  if (behindPP >= cfg.red_threshold_pp) return 1;
  if (behindPP >= cfg.yellow_threshold_pp) return 2;
  return 3;
}

function scoreFromBehind(behindPP: number, cfg: ScheduleToleranceConfig): number {
  const zeroAt = cfg.score_zero_at_behind_pp ?? KPI_DEFAULTS.schedule.score_zero_at_behind_pp;
  if (behindPP <= 0) return 100;
  const s = 100 - (behindPP * 100) / zeroAt;
  return Math.round(clamp(s, 0, 100));
}

/**
 * Computes the Schedule KPI signal from progress points as-of a date.
 * Note: Uses baseline vs actual percentage complete.
 */
export function computeScheduleKpiSignal(
  points: ProgressPoint[],
  asOfDate: string,
  cfg: ScheduleToleranceConfig
): KpiSignal {
  // Filter out rows where actual is null/undefined — null means "no actual
  // progress entered yet", not "0% complete". Treating null as 0 would
  // falsely flag behind-schedule variance against the baseline.
  const pts = points
    .filter((p) => {
      const act = (p as any).actual_complete ?? (p as any).actual;
      return act !== null && act !== undefined;
    })
    .map((p) => ({
      date: (p as any).reporting_date ?? (p as any).date,
      baseline: (p as any).baseline_complete ?? (p as any).baseline,
      actual: (p as any).actual_complete ?? (p as any).actual,
    }));

  const cur = latestAsOf(pts, asOfDate);
  if (!cur) {
    return {
      score_0_100: null,
      indicator_color_value: 0,
      indicator_trend_value: 0,
      as_of_date: asOfDate,
      explain: { headline: 'No schedule progress data available as of this date.' },
    };
  }

  const prev = previousBeforeAsOf(pts, asOfDate);
  const behindPP = (cur.baseline ?? 0) - (cur.actual ?? 0);
  const score = scoreFromBehind(behindPP, cfg);
  const colorValue = indicatorColorValueFromBehind(behindPP, cfg);

  /* Trend computation — DEV-814 bug fix.
   *
   * Two principles after the audit:
   *
   *   1. User-input override: if the user has not entered new actual data
   *      since the previous reporting period (i.e., cur.actual === prev.actual
   *      within epsilon), trend is 0 regardless of how baseline moved.
   *      This prevents the indicator from "flipping" purely because the
   *      planned baseline curve advanced while the user was on vacation.
   *      Skip flagged this with his L4E project: actual=11.7% for 4 weeks,
   *      baseline rising — old code interpreted as "behind growing" → arrow
   *      down. New code interprets as "no user input → no trend change".
   *
   *   2. Behind-PP delta when actual DID change: when the user did enter new
   *      data, the trend reflects whether the gap to baseline (behindPP) is
   *      improving (-1, less behind) or worsening (+1, more behind). Same
   *      logic as before, just gated on actual having moved.
   *
   * Epsilon defaults bumped from 0.25 to 1.0 percentage points to be more
   * forgiving of small reporting-period baseline drift. Per-project override
   * remains via cfg.trend_epsilon_pp.
   */
  const eps = cfg.trend_epsilon_pp ?? KPI_DEFAULTS.schedule.trend_epsilon_pp;
  let trend: -1 | 0 | 1 = 0;
  if (prev) {
    const actualDelta = (cur.actual ?? 0) - (prev.actual ?? 0);
    const userEnteredNewData = Math.abs(actualDelta) > eps;
    if (userEnteredNewData) {
      const prevBehind = (prev.baseline ?? 0) - (prev.actual ?? 0);
      if (behindPP < prevBehind - eps) trend = 1;
      else if (behindPP > prevBehind + eps) trend = -1;
    }
    /* If user did not enter new data, trend stays at 0 (initialized above). */
  }

  return {
    score_0_100: score,
    indicator_color_value: colorValue,
    indicator_trend_value: trend,
    as_of_date: cur.date,
    explain: {
      headline:
        behindPP <= 0
          ? 'On or ahead of baseline progress.'
          : `Behind baseline by ${behindPP.toFixed(1)} percentage points as of ${cur.date}.`,
      details: {
        baseline_complete: cur.baseline,
        actual_complete: cur.actual,
        behind_pp: Number(behindPP.toFixed(3)),
        previous_date: prev?.date ?? null,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// SPI (Schedule Performance Index) — EVM-lite
// SPI = Actual% / Planned% at each reporting date.
// ---------------------------------------------------------------------------

export type SpiHistoryPoint = {
  date: string;
  spi: number;
  baseline_pct: number;
  actual_pct: number;
  /** True when baseline < early_maturity_guard_pct — SPI is mathematically
   *  valid but unreliable due to near-zero denominator. */
  belowGuard?: boolean;
};

export type SpiToleranceConfig = {
  /** SPI >= this is green. Default 0.95. */
  warning_threshold: number;
  /** SPI < this is red. Default 0.90. */
  critical_threshold: number;
  /** Suppress SPI when planned% < this (avoids divide-by-near-zero). Default 5. */
  early_maturity_guard_pct: number;
};

export const SPI_TOLERANCE_DEFAULTS: SpiToleranceConfig = {
  warning_threshold: KPI_DEFAULTS.spi.warning_threshold,
  critical_threshold: KPI_DEFAULTS.spi.critical_threshold,
  early_maturity_guard_pct: KPI_DEFAULTS.spi.early_maturity_guard_pct,
};

/**
 * Computes SPI = Actual% / Planned% at each reporting date.
 *
 * Early maturity guardrail: points where baseline_complete < guard threshold
 * are excluded to prevent noise when dividing by near-zero.
 *
 * The input `points` array uses the same ProgressPoint type that feeds the
 * progress chart — no additional data ingestion required.
 */
export function computeSpiHistorySeries(
  points: ProgressPoint[],
  config?: Partial<SpiToleranceConfig>,
): SpiHistoryPoint[] {
  const cfg: SpiToleranceConfig = { ...SPI_TOLERANCE_DEFAULTS, ...config };
  if (!Array.isArray(points) || points.length === 0) return [];

  const sorted = [...points].sort((a, b) =>
    String(a.reporting_date).localeCompare(String(b.reporting_date)),
  );

  const out: SpiHistoryPoint[] = [];
  for (const p of sorted) {
    const baseline = Number(p.baseline_complete);
    const actual = Number(p.actual_complete);
    if (!Number.isFinite(baseline) || !Number.isFinite(actual)) continue;

    // Skip truly-zero baselines (SPI is undefined when planned = 0).
    if (baseline <= 0) continue;

    const spi = actual / baseline;
    if (!Number.isFinite(spi)) continue;

    out.push({
      date: p.reporting_date,
      spi,
      baseline_pct: baseline,
      actual_pct: actual,
      // Early maturity flag — chart renders these with lower visual weight.
      belowGuard: baseline < cfg.early_maturity_guard_pct,
    });
  }
  return out;
}

/**
 * Returns the most recent SPI point at or before `asOfDate`,
 * or null if no data is available.
 *
 * Points flagged `belowGuard` are skipped — they're included in the
 * history for chart display but should not drive the gauge or score.
 */
export function getCurrentSpiAsOf(
  history: SpiHistoryPoint[],
  asOfDate: string,
): SpiHistoryPoint | null {
  if (!history.length) return null;
  let best: SpiHistoryPoint | null = null;
  for (const p of history) {
    if (p.belowGuard) continue;
    if (p.date <= asOfDate && (best === null || p.date > best.date)) {
      best = p;
    }
  }
  return best;
}

/**
 * Returns the SPI color band code for a given SPI value.
 * 0 = no data, 1 = red (critical), 2 = yellow (warning), 3 = green (on track).
 */
export function getSpiColorBand(
  spi: number,
  config?: Partial<SpiToleranceConfig>,
): 0 | 1 | 2 | 3 {
  const cfg: SpiToleranceConfig = { ...SPI_TOLERANCE_DEFAULTS, ...config };
  if (!Number.isFinite(spi)) return 0;
  if (spi < cfg.critical_threshold) return 1;
  if (spi < cfg.warning_threshold) return 2;
  return 3;
}
